// src/components/CommentTable.js
import React from 'react';

const CommentTable = ({ entries, onDeleteEntry, isLoading, user, onError }) => {
  const handleDelete = async (entryId, authorId) => {
    // Only allow users to delete their own entries
    if (authorId !== user.uid) {
      onError("You can only delete your own entries.");
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await onDeleteEntry(entryId);
        // The UI will update automatically through the real-time listener
      } catch (error) {
        console.error("Error deleting entry:", error);
        onError("Failed to delete entry. Please try again.");
      }
    }
  };
  
  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch (e) {
      return "Invalid date";
    }
  };
  
  if (isLoading) {
    return (
      <div className="table-container">
        <h3>Comments and Citations</h3>
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading entries...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="table-container">
      <h3>Comments and Citations</h3>
      {entries.length === 0 ? (
        <p className="no-entries">No entries yet. Be the first to add one!</p>
      ) : (
        <table className="entries-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Author</th>
              <th>Content</th>
              <th>Details</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className={entry.type}>
                <td>
                  <span className={`type-badge ${entry.type}`}>
                    {entry.type === 'comment' ? 'Comment' : 'Citation'}
                  </span>
                </td>
                <td className="author-cell">
                  {entry.type === 'comment' ? entry.author : entry.author}
                </td>
                <td className="content-cell">
                  {entry.type === 'comment' ? (
                    <p>{entry.text}</p>
                  ) : (
                    <div className="citation-content">
                      <p className="citation-text">"{entry.citationText}"</p>
                      <p className="article-title">From: {entry.articleTitle}</p>
                    </div>
                  )}
                </td>
                <td className="details-cell">
                  {entry.type === 'citation' && (
                    <div className="citation-details">
                      {entry.publication && <p className="publication">{entry.publication}</p>}
                      {entry.publicationDate && (
                        <p className="publication-date">
                          Published: {new Date(entry.publicationDate).toLocaleDateString()}
                        </p>
                      )}
                      {entry.url && (
                        <a 
                          href={entry.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="source-link"
                        >
                          View Source
                        </a>
                      )}
                    </div>
                  )}
                </td>
                <td className="date-cell">{formatDate(entry.timestamp)}</td>
                <td className="actions-cell">
                  {((entry.type === 'comment' && entry.authorId === user.uid) ||
                    (entry.type === 'citation' && entry.submitterId === user.uid)) && (
                    <button 
                      onClick={() => handleDelete(
                        entry.id, 
                        entry.type === 'comment' ? entry.authorId : entry.submitterId
                      )}
                      className="delete-button"
                      aria-label="Delete entry"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CommentTable;